import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * Helper to convert Blob to Base64 String
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Utility to reliably download or share files (CSV, PDF, etc.)
 * across Desktop Browsers, Mobile Web Browsers, and Native WebViews (Capacitor/Android/iOS).
 */
export async function downloadOrShareFile(options: {
  filename: string;
  blob: Blob;
  mimeType: string;
}): Promise<boolean> {
  const { filename, blob, mimeType } = options;

  // 1. Native Mobile Platform (Capacitor on Android / iOS)
  if (Capacitor.isNativePlatform()) {
    try {
      const base64Data = await blobToBase64(blob);
      const savedFile = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Cache,
      });

      await Share.share({
        title: filename,
        url: savedFile.uri,
      });

      return true;
    } catch (nativeErr: any) {
      if (
        nativeErr?.name === 'AbortError' ||
        nativeErr?.message?.toLowerCase().includes('cancel') ||
        nativeErr?.message?.toLowerCase().includes('dismiss')
      ) {
        return true;
      }
      console.warn("Capacitor native share/filesystem failed, falling back:", nativeErr);
    }
  }

  // Detect if user is on a mobile device browser (e.g. mobile phone/tablet)
  const isMobileBrowser = typeof navigator !== 'undefined' && 
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // 2. Web Share API (Mobile Web Browsers Only)
  if (isMobileBrowser && typeof navigator !== 'undefined' && (navigator as any).canShare) {
    try {
      const file = new File([blob], filename, { type: mimeType });
      if ((navigator as any).canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: filename,
        });
        return true;
      }
    } catch (shareErr: any) {
      if (shareErr?.name === 'AbortError') {
        return true;
      }
      console.warn("Web Share API failed, falling back to blob download:", shareErr);
    }
  }

  // 3. Standard Blob URL Download Fallback (Desktop Browsers)
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 1000);
    return true;
  } catch (err) {
    console.error("Failed to trigger file download:", err);
    return false;
  }
}
